import { isEmpty } from 'lodash';

import type { CSSProperties } from 'react';

import { IAccount, INavigation, ISideKickStatus } from '@share/common-types';
import { Routes } from '@share/constants';
import { isDisplayCertificates } from '@share/utils';

import type { AccountSectionId, IAccountSection, IAccountSectionGroup } from './types';

const SECTION_SLUGS: Record<AccountSectionId, string> = {
  overview: '',
  bookings: 'reservations',
  history: 'travel-history',
  quotes: 'quotes',
  saved: 'saved-properties',
  certificates: 'certificates',
  reports: 'reports',
  website: 'website',
  manualUpload: 'manual-upload',
  supplier: 'supplier-registration',
  sidekick: 'sidekick',
  profile: 'profile',
  authorized: 'authorized-users',
  payment: 'payment-methods',
  password: 'password',
};

// Older account URLs that must keep landing on the right section.
const SLUG_ALIASES: Record<string, AccountSectionId> = {
  'test-drive-manage': 'sidekick',
  // Commissions was folded into My reports.
  commissions: 'reports',
};

/**
 * Travel agents who get "My Dashboard" in the main menu: `isTravelAgent` (menu API) within one of the
 * account's Levarti orgs. Same rule as `GetMainMenu` (share/utils/menu.tsx) and the dashboard's
 * agent-only tabs, so the hub's Commissions section appears for exactly those members.
 */
export const isAccountTravelAgent = (account: IAccount | null | undefined, items: INavigation | null | undefined): boolean =>
  !!items?.isTravelAgent && !!account?.levartiOrgIds?.includes(items?.organizationId as number);

const isSavedPropertiesVisible = (account: IAccount, items: INavigation | null | undefined): boolean => {
  if (account.isPublicAccountType || !account.displaySavedProperties) {
    return false;
  }
  if (!account.isRSITemplate) {
    return true;
  }

  const orgIdList = account.displaySavedPropertiesRsiOrgidList || [];
  const siteIdList = account.displaySavedPropertiesRsiSiteidList || [];
  if (!orgIdList.length && !siteIdList.length) {
    return true;
  }

  return orgIdList.includes(items?.organizationId?.toString() as string) ||
    siteIdList.includes(items?.siteId?.toString() as string);
};

/**
 * The My Account sections a member can see, in display order. Single source of truth for both the
 * legacy tabbed page and the hub, so the two can't drift apart.
 */
export const getAccountSections = (
  account: IAccount | null | undefined,
  items: INavigation | null | undefined,
): IAccountSection[] => {
  if (!account) {
    return [];
  }

  const isMLM = !!items?.isMLM;
  const isRSITemplate = !!account.isRSITemplate;
  const sections: IAccountSection[] = [
    { id: 'overview', group: null, slug: SECTION_SLUGS.overview, labelId: 'my.account.hub.section.overview' },
  ];

  // Travel agents: My Dashboard's tools live in the hub (My Dashboard redirects here).
  const isAgent = isAccountTravelAgent(account, items);
  if (isAgent) {
    sections.push(
      { id: 'reports', group: 'agent', slug: SECTION_SLUGS.reports, labelId: 'my.account.hub.section.reports' },
      { id: 'website', group: 'agent', slug: SECTION_SLUGS.website, labelId: 'my.account.hub.section.website' },
      { id: 'manualUpload', group: 'agent', slug: SECTION_SLUGS.manualUpload, labelId: 'my.account.hub.section.manual_upload' },
      { id: 'supplier', group: 'agent', slug: SECTION_SLUGS.supplier, labelId: 'my.account.hub.section.supplier' },
    );
  }

  if (!isRSITemplate || !account.isMergeHistoryTabs) {
    sections.push({
      id: 'bookings',
      group: 'trips',
      slug: SECTION_SLUGS.bookings,
      labelId: isMLM ? 'reservation.search.title' : 'my.account.hub.section.bookings',
    });
  }

  const hasHistoryAndQuotes = isRSITemplate && !isMLM;

  if (hasHistoryAndQuotes) {
    sections.push({ id: 'quotes', group: 'trips', slug: SECTION_SLUGS.quotes, labelId: 'my.quotes' });
  }

  if (isSavedPropertiesVisible(account, items)) {
    sections.push({ id: 'saved', group: 'trips', slug: SECTION_SLUGS.saved, labelId: 'saved.properties' });
  }

  // Past trips sit last in the Trips group, after saved properties.
  if (hasHistoryAndQuotes) {
    sections.push({ id: 'history', group: 'trips', slug: SECTION_SLUGS.history, labelId: 'my.travel.history' });
  }

  if (isDisplayCertificates(account)) {
    sections.push({ id: 'certificates', group: 'certificates', slug: SECTION_SLUGS.certificates, labelId: 'my.account.hub.section.certificates' });
  }

  sections.push({ id: 'profile', group: 'account', slug: SECTION_SLUGS.profile, labelId: 'profile' });

  if (!items?.isRemoveAuthorizedUsers) {
    sections.push({ id: 'authorized', group: 'account', slug: SECTION_SLUGS.authorized, labelId: 'authorized.users' });
  }

  sections.push({ id: 'payment', group: 'account', slug: SECTION_SLUGS.payment, labelId: 'my.account.hub.section.payment' });

  // Agents are MLM but My Dashboard has always let them change their password, so they keep it here.
  if (!isMLM || isAgent) {
    sections.push({ id: 'password', group: 'account', slug: SECTION_SLUGS.password, labelId: 'manage.password' });
  }

  if (items?.sideKickStatus === ISideKickStatus.PARENT) {
    sections.push({
      id: 'sidekick',
      group: 'account',
      slug: SECTION_SLUGS.sidekick,
      labelId: isMLM ? 'test.drive.manager' : 'my.sidekicks',
      label: !isEmpty(items?.sideKickName) ? items?.sideKickName : undefined,
    });
  }

  return sections;
};

export const groupAccountSections = (sections: IAccountSection[]): IAccountSectionGroup[] => {
  const order: { id: IAccountSectionGroup['id']; labelId: string | null }[] = [
    { id: null, labelId: null },
    { id: 'agent', labelId: 'my.account.hub.group.agent' },
    { id: 'trips', labelId: 'my.account.hub.group.trips' },
    { id: 'wallet', labelId: 'my.account.hub.group.wallet' },
    { id: 'certificates', labelId: 'my.account.hub.group.certificates' },
    { id: 'account', labelId: 'my.account.hub.group.account' },
  ];

  return order
    .map(group => ({ ...group, sections: sections.filter(section => section.group === group.id) }))
    .filter(group => group.sections.length > 0);
};

/** Reads the section from `/:accountUsername/account/:slug/...`; anything unknown is Overview. */
export const getAccountSectionIdFromPathname = (pathname: string): AccountSectionId => {
  const slug = pathname.split('/')[3] || '';
  if (SLUG_ALIASES[slug]) {
    return SLUG_ALIASES[slug];
  }

  const match = (Object.keys(SECTION_SLUGS) as AccountSectionId[]).find(id => SECTION_SLUGS[id] === slug);
  return match || 'overview';
};

export const getAccountSectionPath = (accountUsername: string, section: IAccountSection): string =>
  `/${accountUsername}${Routes.RsiAccount}${section.slug ? `/${section.slug}` : ''}`;

/**
 * Parses the date part of an API date as a local date, so it never shifts a day. Accepts
 * `2026-10-12`, `2026-10-12T00:00:00` and the reservations list's `10/12/2026` (MM/DD/YYYY).
 */
export const parseApiDate = (value: string | null | undefined): Date | null => {
  const text = value || '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }

  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text);
  if (us) {
    return new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  }

  return null;
};

/** Tenant button colors as hub tokens; applied on the hub root and on overlays rendered outside it. */
export const getHubThemeStyle = (account: IAccount | null | undefined): CSSProperties => ({
  ...(account?.buttonColor ? { '--hub-primary': account.buttonColor } : {}),
  ...(account?.buttonTextColor ? { '--hub-on-primary': account.buttonTextColor } : {}),
} as CSSProperties);
